import { useCallback, useMemo, Dispatch, MouseEvent } from 'react';
import {
  useEngineInstance,
  useSubjectId
} from '@surveycake/react-logic-engine';
import { useOnKeyDownEnter } from '@surveycake/rhooks';
import { useSurveyConfig } from 'app/core/survey-config';
import { useHandleAnswerFinish } from 'app/modules/subject';
import { createUseInputRefAndSetAnswer } from 'app/modules/subject';

const useDigitInputAnswerPlaceholder = () =>
  useSurveyConfig().wording.message.placeholder.input.number;

function isSupportBadInput(inputEl: HTMLInputElement | HTMLTextAreaElement) {
  return typeof inputEl.validity.badInput !== 'undefined';
}

const useDigitInputInputRefAndSetAnswer = createUseInputRefAndSetAnswer<
  number,
  HTMLInputElement
>(
  (inputEl, value) => {
    if (value === undefined) {
      inputEl.value = '';
    } else if (!isNaN(value)) {
      if (isSupportBadInput(inputEl)) {
        inputEl.valueAsNumber = value;
      } else {
        inputEl.value = `${value}`;
      }
    }
  },
  inputEl => {
    const {
      value,
      validity: { badInput }
    } = inputEl;
    const supportBadInput = isSupportBadInput(inputEl);
    const valueAsNumber = supportBadInput ? inputEl.valueAsNumber : +value;

    if (!supportBadInput) {
      if (!value) {
        return [];
      } else if (isNaN(valueAsNumber)) {
        return [NaN];
      }

      return [valueAsNumber];
    }

    if (badInput) {
      return [NaN];
    } else if (isNaN(valueAsNumber)) {
      return [];
    }

    return [valueAsNumber];
  }
);

export function useDigitInputAnswer(setArrowShow: Dispatch<boolean>) {
  const engine = useEngineInstance();
  const id = useSubjectId();
  const { min, max } = useMemo(() => {
    const data = engine.getSubjectData(id);
    return {
      min: data.lowerbounds!,
      max: data.upperbounds!
    };
  }, [engine, id]);
  const placeholder = useDigitInputAnswerPlaceholder();
  const { inputRef, onChange, setAnswer } = useDigitInputInputRefAndSetAnswer(
    engine,
    id
  );
  const handleAnswerFinish = useHandleAnswerFinish(engine, id);
  const onKeyDown = useOnKeyDownEnter<HTMLInputElement>(
    useCallback(async event => {
      event.preventDefault();
      await setAnswer();
      handleAnswerFinish();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );
  const onBlur = async () => {
    await setAnswer();
    setArrowShow(false);
  };
  const onFocus = () => {
    setArrowShow(true);
  };
  const onMouseLeave = (e: MouseEvent<HTMLInputElement>) => {
    const target = e.target;
    const focusedDom = document.activeElement;
    // onfocus 的時候滑鼠離開不觸發
    if (target !== focusedDom) {
      setArrowShow(false);
    }
  };
  const onMouseOver = () => {
    setArrowShow(true);
  };

  return {
    ref: inputRef,
    min,
    max,
    placeholder,
    onChange,
    onBlur,
    onFocus,
    onMouseLeave,
    onMouseOver,
    onKeyDown,
    'data-subject-answer': id
  };
}
